// TRONA - Corporate Sidebar (shadcn/ui)
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  X,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  ChevronUp,
  User,
  LayoutDashboard,
  FileText,
  Gift,
  HelpCircle,
  Settings,
  Plus,
  Upload,
  Landmark,
} from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { ROUTES } from '../../constants/routes';
import { useCompany } from 'contexts/CompanyContext';
import { buildLogoutReturnTo } from 'utils/authRedirect';
import CompanySelector from './components/CompanySelector';
import SidebarSection from './components/SidebarSection';

// Navigation link component
function SidebarLink({
  to,
  icon: Icon,
  label,
  collapsed,
  isActive,
  disabled,
  badge,
  isPrimary,
}) {
  const content = (
    <div
      className={cn(
        'relative flex items-center rounded-xl',
        'group transition-all duration-200',
        isPrimary
          ? cn(
              'bg-primary-500 hover:bg-primary-600',
              collapsed ? 'mx-auto my-1.5 w-11' : 'my-1'
            )
          : disabled
          ? 'cursor-not-allowed text-neutral-500 opacity-75'
          : isActive
          ? 'bg-primary-100/95 shadow-sm ring-1 ring-primary-300/80 dark:bg-primary-900/25'
          : 'hover:bg-white hover:shadow-sm dark:hover:bg-neutral-800/80',
        collapsed && !isPrimary && 'mx-auto w-11'
      )}
    >
      <div
        className={cn(
          'flex w-full cursor-pointer items-center',
          collapsed ? 'justify-center py-2.5' : 'px-3 py-2.5'
        )}
      >
        {/* Icon */}
        <span
          className={cn(
            'flex items-center justify-center',
            'h-5 w-5 flex-shrink-0',
            isPrimary
              ? 'text-white'
              : isActive
              ? 'text-primary-600'
              : 'text-neutral-600 group-hover:text-neutral-900 dark:text-neutral-400 dark:group-hover:text-neutral-200'
          )}
          title={collapsed ? label : undefined}
        >
          <Icon className="h-5 w-5" />
        </span>

        {/* Label */}
        {!collapsed && (
          <span
            className={cn(
              'ml-3 text-sm leading-none',
              isPrimary
                ? 'font-semibold text-white'
                : isActive
                ? 'font-semibold text-primary-700 dark:text-white'
                : 'font-medium text-neutral-700 group-hover:text-neutral-950 dark:text-neutral-300 dark:group-hover:text-white'
            )}
          >
            {label}
          </span>
        )}

        {/* Badge */}
        {badge && !collapsed && (
          <span
            className={cn(
              'ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold',
              badge === 'soon'
                ? 'bg-neutral-200 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400'
                : 'bg-primary-500 text-white'
            )}
          >
            {badge === 'soon' ? 'Soon' : badge}
          </span>
        )}
      </div>

      {/* Active Indicator */}
      {isActive && !isPrimary && !collapsed && (
        <div className="absolute right-0 top-1/2 h-7 w-1 -translate-y-1/2 rounded-l-full bg-primary-500" />
      )}
    </div>
  );

  if (disabled) {
    return <div className="cursor-not-allowed">{content}</div>;
  }

  return <Link to={to}>{content}</Link>;
}

const Sidebar = ({ open, onClose, collapsed, onToggleCollapse, variant }) => {
  const { user, logout } = useAuth0();
  const { t } = useTranslation();
  const location = useLocation();
  const { selectedCompanyId, hasCompanies } = useCompany();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const returnTo = buildLogoutReturnTo();

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logout({ logoutParams: { returnTo } });
  };

  // Check if route is active
  const isActive = (path) => location.pathname.includes(path);

  // Company-scoped routes (only available when a company is selected)
  const companyRoutes = selectedCompanyId
    ? [
        {
          to: ROUTES.COMPANY_DASHBOARD(selectedCompanyId),
          icon: LayoutDashboard,
          label: t('navigation.dashboard'),
          path: 'dashboard',
        },
        {
          to: ROUTES.COMPANY_DOCUMENTS(selectedCompanyId),
          icon: FileText,
          label: t('navigation.documents'),
          path: 'documentos',
        },
      ]
    : [];

  // Service routes (company-scoped when company is selected)
  const serviceRoutes = selectedCompanyId
    ? [
        {
          to: ROUTES.COMPANY_BANKING(selectedCompanyId),
          icon: Landmark,
          label: t('navigation.bankAccount', 'Cuentas'),
          path: 'banking',
        },
        {
          to: ROUTES.COMPANY_PERKS(selectedCompanyId),
          icon: Gift,
          label: t('navigation.perks', 'Perks'),
          path: 'perks',
        },
      ]
    : [
        {
          to: ROUTES.HOME,
          icon: Landmark,
          label: t('navigation.bankAccount', 'Cuentas'),
          path: 'banking',
          disabled: true,
        },
        {
          to: ROUTES.HOME,
          icon: Gift,
          label: t('navigation.perks', 'Perks'),
          path: 'perks',
          disabled: true,
        },
      ];

  // Account routes
  const accountRoutes = [
    {
      to: ROUTES.SOPORTE,
      icon: HelpCircle,
      label: t('help'),
      path: 'soporte',
    },
    {
      to: ROUTES.CUENTA,
      icon: Settings,
      label: t('settings'),
      path: 'cuenta',
    },
  ];

  return (
    <div
      className={cn(
        'fixed !z-50 min-h-full transition-all duration-300 ease-in-out',
        'md:!z-50 lg:!z-50 xl:!z-0',
        variant === 'auth' ? 'xl:hidden' : 'xl:block',
        open ? '' : '-translate-x-full xl:translate-x-0'
      )}
    >
      {/* Sidebar Card */}
      <div
        className={cn(
          'relative mt-4 h-[calc(100vh-2rem)]',
          'rounded-2xl transition-all duration-300 ease-out',
          'border border-neutral-200/95 bg-white/95 shadow-[0_28px_70px_-46px_rgba(21,24,39,0.46)] backdrop-blur-xl',
          'dark:border-neutral-800/90 dark:bg-neutral-950/90 dark:shadow-black/40',
          collapsed ? 'ml-3 w-[72px]' : 'ml-4 w-[280px]'
        )}
      >
        {/* Close button - Mobile only (outside scroll container) */}
        <button
          className={cn(
            'absolute right-3 top-3 z-10 rounded-lg p-2',
            'text-neutral-500 hover:text-neutral-900',
            'transition-colors hover:bg-neutral-100',
            'dark:text-neutral-400 dark:hover:text-white',
            'dark:hover:bg-neutral-800',
            'xl:hidden'
          )}
          onClick={onClose}
          aria-label={t('sidebar.closeSidebar')}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Collapse toggle button - Desktop only (outside scroll container) */}
        <button
          className={cn(
            'absolute z-10 rounded-lg p-1.5',
            'text-neutral-400 hover:text-primary-500',
            'border border-neutral-200 hover:border-primary-400',
            'transition-all duration-200 hover:bg-primary-50',
            'dark:border-neutral-700 dark:hover:border-primary-500',
            'dark:hover:bg-primary-900/20',
            'hidden items-center justify-center xl:flex',
            collapsed ? 'left-1/2 top-4 -translate-x-1/2' : 'right-4 top-4'
          )}
          onClick={onToggleCollapse}
          title={
            collapsed
              ? t('sidebar.expandSidebar')
              : t('sidebar.collapseSidebar')
          }
          aria-label={
            collapsed
              ? t('sidebar.expandSidebar')
              : t('sidebar.collapseSidebar')
          }
        >
          {collapsed ? (
            <PanelLeft className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </button>

        <div className="sidebar-scrollbar h-full overflow-y-auto overflow-x-hidden">
          <div className="flex h-full min-h-full flex-col justify-between py-5">
            <div>
              {/* Logo */}
              <div
                className={cn(
                  'flex items-center justify-center transition-all duration-300',
                  collapsed ? 'mb-4 mt-14 px-2' : 'mb-6 mt-12 px-6'
                )}
              >
                {collapsed ? (
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl transition-all duration-300 dark:bg-white/95 dark:shadow-[0_14px_34px_-24px_rgba(255,255,255,0.9)] dark:ring-1 dark:ring-white/20">
                    <img
                      src="/brand/icon.png"
                      alt="TRONA"
                      className="h-8 w-8 rounded-xl object-contain transition-all duration-300"
                    />
                  </div>
                ) : (
                  <div className="flex h-14 w-full max-w-[228px] items-center justify-center rounded-2xl px-4 transition-all duration-300 dark:bg-white/95 dark:shadow-[0_18px_42px_-26px_rgba(255,255,255,0.9)] dark:ring-1 dark:ring-white/20">
                    <img
                      src="/brand/full-logo.png"
                      alt="TRONA Logo"
                      className="h-9 max-w-[210px] object-contain transition-all duration-300"
                    />
                  </div>
                )}
              </div>

              {/* Company Selector */}
              <div className="mb-4">
                <CompanySelector collapsed={collapsed} />
              </div>

              {/* Create Company Button */}
              <nav
                className={cn(
                  'transition-all duration-300',
                  collapsed ? 'px-2' : 'px-3'
                )}
                aria-label={t('navigation.mainNavigation')}
              >
                <ul className={cn(collapsed ? 'space-y-1' : 'space-y-0.5')}>
                  <li>
                    <SidebarLink
                      to={ROUTES.NUEVA_EMPRESA}
                      icon={Plus}
                      label={t('navigation.createCompany')}
                      collapsed={collapsed}
                      isActive={isActive('nueva')}
                      isPrimary
                    />
                  </li>
                  <li>
                    <SidebarLink
                      to={ROUTES.IMPORT_EMPRESA}
                      icon={Upload}
                      label={t('navigation.importCompany')}
                      collapsed={collapsed}
                      isActive={isActive('import')}
                    />
                  </li>
                </ul>

                {/* Company-scoped Navigation */}
                {hasCompanies && companyRoutes.length > 0 && (
                  <ul
                    className={cn(
                      'mt-2',
                      collapsed ? 'space-y-1' : 'space-y-0.5'
                    )}
                  >
                    {companyRoutes.map((route) => (
                      <li key={route.path}>
                        <SidebarLink
                          to={route.to}
                          icon={route.icon}
                          label={route.label}
                          collapsed={collapsed}
                          isActive={isActive(route.path)}
                        />
                      </li>
                    ))}
                  </ul>
                )}

                {/* Services Section */}
                <SidebarSection
                  title={t('navigation.services')}
                  collapsed={collapsed}
                >
                  <ul className={cn(collapsed ? 'space-y-1' : 'space-y-0.5')}>
                    {serviceRoutes.map((route) => (
                      <li key={route.path}>
                        <SidebarLink
                          to={route.to}
                          icon={route.icon}
                          label={route.label}
                          collapsed={collapsed}
                          isActive={isActive(route.path)}
                          disabled={route.disabled}
                          badge={route.badge}
                        />
                      </li>
                    ))}
                  </ul>
                </SidebarSection>

                {/* Account Section */}
                <SidebarSection
                  title={t('navigation.account')}
                  collapsed={collapsed}
                >
                  <ul className={cn(collapsed ? 'space-y-1' : 'space-y-0.5')}>
                    {accountRoutes.map((route) => (
                      <li key={route.path}>
                        <SidebarLink
                          to={route.to}
                          icon={route.icon}
                          label={route.label}
                          collapsed={collapsed}
                          isActive={isActive(route.path)}
                        />
                      </li>
                    ))}
                  </ul>
                </SidebarSection>
              </nav>
            </div>

            {/* User Section - Desktop only */}
            {user && (
              <div
                className={cn(
                  'relative border-t border-neutral-200 dark:border-neutral-800',
                  'transition-all duration-300',
                  collapsed ? 'mx-2 px-1 py-3' : 'mx-3 px-2 py-4'
                )}
              >
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl p-2',
                    'text-neutral-800 hover:bg-neutral-100',
                    'dark:text-neutral-300 dark:hover:bg-neutral-800',
                    'transition-colors duration-200',
                    collapsed && 'justify-center'
                  )}
                >
                  {/* Avatar */}
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="h-9 w-9 flex-shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-white">
                      <User className="h-5 w-5" />
                    </div>
                  )}

                  {/* User info - only when not collapsed */}
                  {!collapsed && (
                    <>
                      <div className="flex-1 overflow-hidden text-left">
                        <p className="truncate text-sm font-semibold text-neutral-950 dark:text-white">
                          {user.name || user.email?.split('@')[0]}
                        </p>
                        <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                          {user.email}
                        </p>
                      </div>
                      <ChevronUp
                        className={cn(
                          'h-4 w-4 text-neutral-400 transition-transform duration-200',
                          showUserMenu ? 'rotate-180' : ''
                        )}
                      />
                    </>
                  )}
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div
                    className={cn(
                      'absolute bottom-full left-0 right-0 mb-2 rounded-xl',
                      'border border-neutral-200 bg-white shadow-lg',
                      'dark:border-neutral-700 dark:bg-neutral-800',
                      collapsed ? 'left-1/2 w-48 -translate-x-1/2' : ''
                    )}
                  >
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        setShowLogoutConfirm(true);
                      }}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-xl px-4 py-3',
                        'text-red-600 hover:bg-red-50',
                        'dark:text-red-400 dark:hover:bg-red-900/20',
                        'transition-colors duration-200'
                      )}
                    >
                      <LogOut className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {t('sidebar.logout')}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal - Rendered via Portal for proper z-index */}
      {showLogoutConfirm &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl dark:bg-neutral-800">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <LogOut className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-neutral-900 dark:text-white">
                {t('sidebar.logoutConfirmTitle')}
              </h3>
              <p className="mb-6 text-sm text-neutral-600 dark:text-neutral-400">
                {t('sidebar.logoutConfirmMessage')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 rounded-xl border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100 dark:border-neutral-600 dark:text-neutral-300 dark:hover:bg-neutral-700"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700"
                >
                  {t('sidebar.logout')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Sidebar;
